import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import axiosInstance from "../../helpers/axios";

const API_DETAILS_URL = "/api-analytics/api-details";
const NAME_HELPER = "Must match the billing name logged in api_request";
const NAME_LOCKED_HELPER = "Name is locked — this API has calls in the last 30 days.";
const NAME_LOCKED_TOOLTIP =
  "Calls are logged by API name, so renaming an API that is in use would start logging its calls under a new, unpriced API.";
const EFFECTIVE_FROM_HELPER = "Start date of this rate. If you change the rate but not this date, today is used.";
const EMPTY_FORM = {
  name: "",
  provider: "",
  costPerCall: "",
  category: "",
  baseUrl: "",
  description: "",
  effectiveFrom: "",
  isActive: true,
};

export default function ApiDetailsDialog({ open, row, categories, hasCalls, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(false);

  // Reset the form whenever the dialog is (re)opened; in edit mode prefill it and load the rate history.
  useEffect(() => {
    if (!open) return;
    setForm({
      name: row?.name || "",
      provider: row?.provider || "",
      costPerCall: row?.costPerCall == null ? "" : String(row.costPerCall),
      category: row?.category || "",
      baseUrl: row?.baseUrl || "",
      description: row?.description || "",
      effectiveFrom: row ? row.effectiveFrom || "" : moment().format("YYYY-MM-DD"),
      isActive: row ? row.isActive : true,
    });
    setErrors({});
    setErrorMessage(null);
    setHistory(null);
    setHistoryError(false);
    if (!row) return;

    let ignore = false;
    axiosInstance
      .get(`${API_DETAILS_URL}/${row.id}/rate-history`, { params: { limit: 100 } })
      .then((res) => {
        if (!ignore) setHistory(res?.data?.data || []);
      })
      .catch((err) => {
        console.error("rate-history fetch failed:", err);
        if (!ignore) setHistoryError(true);
      });
    return () => {
      ignore = true;
    };
  }, [open, row]);

  const nameLocked = Boolean(row) && hasCalls;
  // An old category that is no longer allowed shows as empty, so a valid one has to be picked
  const validCategory = categories.some((item) => item.value === form.category) ? form.category : "";

  const setField = (key) => (e) =>
    setForm({ ...form, [key]: key === "isActive" ? e.target.checked : e.target.value });

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!form.provider.trim()) nextErrors.provider = "Provider is required";
    if (form.costPerCall === "") nextErrors.costPerCall = "Rate is required";
    else if (!/^\d+(\.\d{1,2})?$/.test(form.costPerCall)) nextErrors.costPerCall = "Enter 0 or more, max 2 decimals";
    if (!validCategory) nextErrors.category = "Category is required";
    return nextErrors;
  };

  const handleSave = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {
      name: form.name.trim(),
      provider: form.provider.trim(),
      costPerCall: Number(form.costPerCall),
      category: form.category,
      baseUrl: form.baseUrl.trim(),
      description: form.description.trim(),
      effectiveFrom: form.effectiveFrom || null,
      isActive: form.isActive,
    };
    let body;
    if (row) {
      // Edit sends only the changed fields, so the audit log doesn't record fake changes (e.g. NULL -> "")
      body = Object.fromEntries(Object.entries(payload).filter(([key, value]) => (value ?? "") !== (row[key] ?? "")));
      // A rate change with no new date starts today, so api_details and the rate history agree
      if ("costPerCall" in body && !("effectiveFrom" in body)) body.effectiveFrom = moment().format("YYYY-MM-DD");
    } else {
      // Add leaves out empty optional fields instead of saving them as ""
      body = Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== "" && value !== null));
    }

    setIsSaving(true);
    setErrorMessage(null);
    const request = row
      ? axiosInstance.patch(`${API_DETAILS_URL}/${row.id}`, body)
      : axiosInstance.post(API_DETAILS_URL, body);
    request
      .then(() => {
        onSaved(row ? "API updated" : "API added");
        onClose();
      })
      .catch((error) => {
        const message = error?.response?.data?.message;
        if (error?.response?.status === 409) {
          setErrors({ name: message });
        } else {
          setErrorMessage(Array.isArray(message) ? message.join(", ") : message || "Could not save API.");
        }
      })
      .finally(() => setIsSaving(false));
  };

  return (
    // Can't be closed while saving, so a late response never lands in a closed or reopened dialog
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{row ? `Edit — ${row.name}` : "Add new API"}</DialogTitle>
      <DialogContent>
        {errorMessage && <Alert severity="error" sx={{ marginBottom: "16px" }}>{errorMessage}</Alert>}
        <Tooltip title={nameLocked ? NAME_LOCKED_TOOLTIP : ""} placement="top">
          <div>
            <TextField
              fullWidth
              required
              label="Name"
              value={form.name}
              onChange={setField("name")}
              disabled={nameLocked}
              error={Boolean(errors.name)}
              helperText={errors.name || (nameLocked ? NAME_LOCKED_HELPER : NAME_HELPER)}
              // Lets the hover reach the Tooltip wrapper while the field is disabled
              sx={{ marginBottom: "16px", marginTop: "8px", "& .Mui-disabled": { pointerEvents: "none" } }}
            />
          </div>
        </Tooltip>
        <TextField
          fullWidth
          required
          label="Provider"
          value={form.provider}
          onChange={setField("provider")}
          error={Boolean(errors.provider)}
          helperText={errors.provider}
          sx={{ marginBottom: "16px" }}
        />
        <Box sx={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <TextField
            fullWidth
            required
            label="Rate per call"
            type="number"
            value={form.costPerCall}
            onChange={setField("costPerCall")}
            error={Boolean(errors.costPerCall)}
            helperText={errors.costPerCall}
            inputProps={{ step: "0.01", min: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            fullWidth
            required
            label="Category"
            value={validCategory}
            onChange={setField("category")}
            error={Boolean(errors.category)}
            helperText={errors.category}
            sx={{ flex: 1 }}
          >
            {categories.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
          <TextField
            fullWidth
            label="Effective from"
            type="date"
            value={form.effectiveFrom}
            onChange={setField("effectiveFrom")}
            InputLabelProps={{ shrink: true }}
            helperText={EFFECTIVE_FROM_HELPER}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={<Switch color="success" checked={form.isActive} onChange={setField("isActive")} />}
            label={form.isActive ? "Active" : "Inactive"}
            sx={{ flex: 1, alignSelf: "flex-start", marginTop: "8px" }}
          />
        </Box>
        <TextField
          fullWidth
          label="Base URL"
          value={form.baseUrl}
          onChange={setField("baseUrl")}
          sx={{ marginBottom: "16px" }}
        />
        <TextField fullWidth label="Description" value={form.description} onChange={setField("description")} />

        {row && (
          <Box sx={{ marginTop: "20px" }}>
            <Box sx={{ fontWeight: 600, fontSize: 14, marginBottom: "8px" }}>Rate history</Box>
            {historyError ? (
              <Box sx={{ fontSize: 13, color: "error.main" }}>Could not load rate history.</Box>
            ) : history === null ? (
              <Box sx={{ fontSize: 13, color: "text.secondary" }}>Loading…</Box>
            ) : history.length === 0 ? (
              <Box sx={{ fontSize: 13, color: "text.secondary" }}>No rate changes recorded yet.</Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#F8F8F8" }}>
                  <TableRow>
                    <TableCell>Effective from</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell>Changed by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.effectiveFrom}</TableCell>
                      <TableCell align="right">{item.rate == null ? "—" : `₹${Number(item.rate).toFixed(2)}`}</TableCell>
                      <TableCell>{item.changedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving} sx={{ color: "#fff" }}>
          {isSaving ? "Saving..." : "Save API"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
